import { useMemo, useState } from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  blankProgramSeed,
  coerceLocale,
  pickLocalizedText,
  programCloneBody,
  resolveProgramTemplate,
  type EffortScale,
  type Exercise,
  type NewProgram,
  type ProgramModel,
} from "@heraion/shared";

import { busyAria } from "../a11y";
import { difficultyLabel } from "../Library/difficultyLabel";
import { useTemplates } from "../Library/useTemplates";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import { Button } from "../primitives/Button";
import { resolveTheme, type UiTheme } from "../theme";
import { useThemePreferenceContext } from "../ThemeContext";
import { ModelWizard } from "./ModelWizard";
import { makeSetupStyles } from "./setupStyles";

/** «From scratch» (§5.4b): one empty day and four rounds; the name is typed
 *  in the editor. */
const SCRATCH_ROUNDS = 4;

type Stage = { kind: "start" } | { kind: "models" } | { kind: "templates" } | { kind: "wizard"; model: ProgramModel };

type EntryState = { disabled?: boolean; busy?: boolean };

// «New program» (#336 slice 5, #351, brief §5.4b): from a model (its
// wizard), from a public template (the same copy the Library makes), or from
// scratch. Models and templates are rows in the DB, read once on mount; an
// entry with nothing to offer stays, and says why.
export function NewProgramStart({
  dark,
  apiFetch,
  catalog,
  effortScale,
  onCreate,
  onCancel,
}: {
  dark: boolean;
  apiFetch: ProgramApiFetch;
  catalog: ReadonlyArray<Exercise> | undefined;
  effortScale: EffortScale;
  onCreate: (seed: NewProgram) => void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = coerceLocale(i18n.language);
  const family = useThemePreferenceContext()?.preference.family ?? "brand";
  const theme = resolveTheme(dark, family);
  const styles = useMemo(() => makeSetupStyles(theme), [theme]);
  const local = useMemo(() => makeStyles(theme), [theme]);
  const read = useTemplates({ apiFetch, kinds: ["program", "model"] });
  const [stage, setStage] = useState<Stage>({ kind: "start" });
  const toStart = () => setStage({ kind: "start" });

  if (stage.kind === "wizard") {
    return (
      <ModelWizard
        theme={theme}
        model={stage.model}
        catalog={catalog}
        effortScale={effortScale}
        onCreate={onCreate}
        onBack={() => setStage({ kind: "models" })}
      />
    );
  }

  const titleOf = (key: "models" | "templates") =>
    key === "models"
      ? t("editorV3.model.fromModel", { defaultValue: "From a model" })
      : t("editorV3.model.fromTemplate", { defaultValue: "From a template" });
  const entry = (key: string, title: string, body: string, onPress: () => void, state: EntryState = {}) => (
    <Pressable
      key={key}
      accessibilityRole="button"
      accessibilityLabel={`${title}. ${body}`}
      accessibilityState={{ disabled: state.disabled === true, busy: state.busy === true }}
      // RNW drops accessibilityState: these reach the web.
      aria-disabled={state.disabled === true}
      {...busyAria(state.busy)}
      disabled={state.disabled}
      onPress={onPress}
      style={({ pressed }) => [local.entry, pressed && local.pressed]}
    >
      <Text style={state.disabled ? local.entryTitleOff : local.entryTitle}>{title}</Text>
      {/* The body is what changes loading, error, empty, ready: said aloud. */}
      <Text style={styles.hint} accessibilityLiveRegion="polite" aria-live="polite">
        {body}
      </Text>
    </Pressable>
  );
  const back = (
    <Button theme={theme} variant="ghost" label={t("editorV3.tetrad.back", { defaultValue: "Back" })} onPress={toStart} full />
  );

  if (stage.kind === "models" || stage.kind === "templates") {
    const models = stage.kind === "models";
    return (
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title} accessibilityRole="header">
          {titleOf(models ? "models" : "templates")}
        </Text>
        {models
          ? read.models.map((m) =>
              entry(m.id, pickLocalizedText(m.name, lang), pickLocalizedText(m.description, lang), () =>
                setStage({ kind: "wizard", model: m }),
              ),
            )
          : read.programTemplates.map((tpl) => {
              const resolved = resolveProgramTemplate(tpl, lang);
              return entry(
                tpl.id,
                resolved.name,
                `${difficultyLabel(tpl.difficulty, t)} · ${resolved.description}`,
                // The Library's copy, as a draft the editor opens.
                () => onCreate(programCloneBody(resolved, { status: "draft" })),
              );
            })}
        {back}
      </ScrollView>
    );
  }

  // One read for both lists: loading, error and empty speak in each entry.
  const listEntry = (key: "models" | "templates", count: number, body: string, none: string) =>
    read.status === "loading"
      ? entry(key, titleOf(key), t("editorV3.model.loading", { defaultValue: "Loading…" }), () => {}, {
          disabled: true,
          busy: true,
        })
      : read.status === "error"
        ? entry(key, titleOf(key), t("editorV3.model.loadError", { defaultValue: "Could not load. Tap to try again." }), read.reload)
        : count === 0
          ? entry(key, titleOf(key), none, () => {}, { disabled: true })
          : entry(key, titleOf(key), body, () => setStage({ kind: key }));

  return (
    <View style={styles.container}>
      <Text style={styles.title} accessibilityRole="header">
        {t("program.newTitle", { defaultValue: "New program" })}
      </Text>
      {listEntry(
        "models",
        read.models.length,
        t("editorV3.model.fromModelBody", {
          defaultValue: "The model writes the days and the rounds. You choose the level, the time per session and the exercises.",
        }),
        t("editorV3.model.noModel", { defaultValue: "No model yet." }),
      )}
      {listEntry(
        "templates",
        read.programTemplates.length,
        t("editorV3.model.fromTemplateBody", { defaultValue: "A copy of a public program, to change as you like." }),
        t("editorV3.model.noTemplate", { defaultValue: "No template yet." }),
      )}
      {entry(
        "scratch",
        t("editorV3.tetrad.scratchTitle", { defaultValue: "From scratch" }),
        t("editorV3.tetrad.scratchBody", { defaultValue: "One empty day and four rounds." }),
        () => onCreate(blankProgramSeed("", SCRATCH_ROUNDS, 1)),
      )}
      <Button theme={theme} variant="ghost" label={t("program.cancel", { defaultValue: "Cancel" })} onPress={onCancel} full />
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    entry: {
      minHeight: 44,
      padding: s.s4,
      gap: s.s1,
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: r.r3,
      backgroundColor: p.surface,
    },
    pressed: { backgroundColor: p.surface2 },
    entryTitle: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "700" },
    // Nothing to open: quieter, and its line says why.
    entryTitleOff: { color: p.ink3, fontSize: ty.sizes.base, fontWeight: "700" },
  });
}
